"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { AluumeMark } from "@aluume/shared/components/brand/AluumeMark";

const NAV: { href: string; label: string; external?: boolean }[] = [
  { href: "/bathe", label: "Bathe" },
  // Temporary: Thermae points at the internal /100 founder page until Thermae
  // bookings go live. Restore the marketing URL then:
  // { href: "https://www.aluume.com/thermae-floating-sauna", label: "Thermae", external: true },
  { href: "/100", label: "Thermae" },
  { href: "/vouchers", label: "Gift Vouchers" },
];

function navLinkCls(active: boolean) {
  return `ui-label transition-colors ${active ? "text-ink-on-dark underline underline-offset-4 decoration-1" : "text-ink-on-dark/75 hover:text-ink-on-dark"}`;
}

// ONE profile entry for the whole site. It points at /account — the neutral resolver — not
// at /agent: a founder member is not an agent, and /account is the single place that decides
// which portal (trade / founder / neither) this person belongs in. Signed out, /account
// forwards to /login, the one sign-in form.
function ProfileButton() {
  return (
    <Link
      href="/account"
      aria-label="Your account"
      title="Your account"
      className="flex h-10 w-10 items-center justify-center rounded-full border border-ink-on-dark/40 text-ink-on-dark transition-colors hover:border-ink-on-dark hover:bg-ink-on-dark/10"
    >
      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20c0-3.6 3.1-5.5 7-5.5s7 1.9 7 5.5" strokeLinecap="round" />
      </svg>
    </Link>
  );
}

// `overlay` makes the bar transparent so it can sit over a hero video/image.
// Default is the solid dark bar for utility pages without a hero.
export function SiteHeader({ overlay = false }: { overlay?: boolean }) {
  const pathname = usePathname();

  return (
    <header
      className="relative z-20 flex w-full items-center justify-between px-6 py-3 text-ink-on-dark md:px-12 md:py-[35px]"
      style={overlay ? { backgroundColor: "transparent" } : { backgroundColor: "#393331" }}
    >
      <a href="https://www.aluume.com" aria-label="Aluume home" className="shrink-0">
        <AluumeMark className="h-9 w-auto" />
      </a>

      {/* Desktop nav */}
      <nav className="hidden items-center gap-8 md:flex">
        {NAV.map((n) => n.external
          ? <a key={n.href} href={n.href} className={navLinkCls(false)}>{n.label}</a>
          : <Link key={n.href} href={n.href} className={navLinkCls(pathname === n.href)}>{n.label}</Link>
        )}
        <ProfileButton />
      </nav>

      {/* Mobile: hamburger menu, with the account entry as its last row. The account entry
          used to be desktop-only, because the only thing behind it was the agent portal —
          a trade tool built to a desktop Figma. That is no longer true: the founder-member
          portal is explicitly mobile-first ("founders are consumers booking a daily visit,
          mostly from a phone"), and without a row here a founder on a phone had NO way to
          reach it from the site at all. /agent still shows its desktop-only notice to
          agents on mobile — that behaviour is untouched. */}
      <div className="flex items-center gap-3 md:hidden">
        <details className="relative">
          <summary className="flex h-9 w-9 cursor-pointer list-none items-center justify-center text-[20px] leading-none text-ink-on-dark [&::-webkit-details-marker]:hidden">☰</summary>
          <nav className="absolute right-0 top-12 z-30 flex w-56 flex-col gap-3 rounded-lg bg-cream p-5 text-left shadow-[0px_18px_40px_-12px_rgba(57,51,49,0.28)]">
            {NAV.map((n) => n.external
              ? <a key={n.href} href={n.href} className="ui-label text-ink-soft hover:text-ink">{n.label}</a>
              : <Link key={n.href} href={n.href} className={`ui-label hover:text-ink ${pathname === n.href ? "text-ink underline underline-offset-4 decoration-1" : "text-ink-soft"}`}>{n.label}</Link>
            )}
            <span className="h-px w-full bg-ink/10" />
            {/* "Member portal", not "100 by Aluume": the menu already carries a "Thermae" row
                pointing at /100, and two entries whose labels are both the membership brand
                would read as the same destination twice. It also matches what /account itself
                calls this door ("Member Portal") — same place, same words.
                SHOWN TO EVERYONE, deliberately. The header is server-rendered on public pages
                and has no idea who is looking; adding an auth lookup here would put a Supabase
                round trip on every marketing page load to hide one menu row. It costs nothing
                to leave visible: /member without a session is bounced to sign-in by the
                middleware, and a signed-in non-member gets the portal's own "no membership
                found" notice (or, if they're a travel agent, a redirect to /agent).
                ONE ROW, not two: the "My account" row pointing at the /account role resolver
                was removed on the owner's instruction. Nothing is stranded — /member is itself
                a resolver for the case that matters, sending an agent on to /agent — but note
                a travel agent's mobile route in is now a link labelled for members. */}
            <Link href="/member" className="ui-label text-ink-soft hover:text-ink">Member portal</Link>
          </nav>
        </details>
      </div>
    </header>
  );
}

// Checkout/confirmation header — identical to SiteHeader.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function CheckoutHeader({ step }: { step?: string }) {
  return <SiteHeader />;
}
